// Task #2211 — UGC Express "Final Videos" section, extracted from
// ugc-campaign-detail.tsx so the save affordance is unit-testable.
//
// Presentation rule: never PLAY the uncaptioned stitch — while captions are
// in flight the tile shows "Adding captions…" instead of the raw cut.
// Save rule is looser on purpose: a save must never be blocked on
// captioning, so the Save button is available for ANY completed render with
// a saveable output (raw output while captions generate; the server prefers
// the captioned cut once it's ready).
import {
  Film,
  Loader2,
  Check,
  Bookmark,
  Download,
  AlertTriangle,
} from "lucide-react";

export type FinalVideoTile = {
  script: any;
  renderId: string;
  /** Playable URL — captioned cut when ready, raw output if captioning failed. */
  url?: string;
  /** True when the render has ANY saveable output (save never blocks on captions). */
  saveable: boolean;
  captioning: boolean;
  failed?: boolean;
};

/** Derive the Final Videos tiles from scripts + latest renders. Pure. */
export function deriveFinalVideoTiles(
  scripts: any[],
  rendersByScript: Record<string, any> | undefined,
): FinalVideoTile[] {
  return (scripts || [])
    .map((script: any) => {
      const r = rendersByScript?.[script.id];
      if (r?.status !== "complete") return null;
      const renderId = r.id as string;
      const capStatus = r.captionedVideoStatus as string | undefined;
      const capUrl = r.captionedVideoUrl as string | undefined;
      const outputUrl = r.outputUrl as string | undefined;
      const saveable = !!((capStatus === "ready" && capUrl) || outputUrl);
      // Captions burned in → play the captioned final.
      if (capStatus === "ready" && capUrl) {
        return { script, renderId, url: capUrl, saveable, captioning: false };
      }
      // Captioning (fal veed) is a separate, flaky post-step. When it FAILS
      // the stitched render itself still succeeded, so deliver the completed
      // uncaptioned final instead of presenting a good video as "failed"
      // (mirrors the per-script view + VE Max fallback to outputUrl).
      if (capStatus === "failed" && outputUrl) {
        return { script, renderId, url: outputUrl, saveable, captioning: false };
      }
      // Otherwise captions are still in flight — keep the slot visible as
      // "Adding captions…" rather than the raw cut. Saving stays available.
      return {
        script,
        renderId,
        url: undefined,
        saveable,
        captioning: capStatus !== "failed",
        failed: capStatus === "failed",
      };
    })
    .filter(Boolean) as FinalVideoTile[];
}

export function UgcFinalVideosSection({
  scripts,
  scriptPlans,
  rendersByScript,
  savedRenderIds,
  savingRenderId,
  onSave,
}: {
  scripts: any[];
  scriptPlans: any[];
  rendersByScript: Record<string, any> | undefined;
  savedRenderIds: Set<string>;
  savingRenderId: string | null;
  onSave: (renderId: string) => void;
}) {
  const finalScripts = deriveFinalVideoTiles(scripts, rendersByScript);
  if (finalScripts.length === 0) return null;
  const readyCount = finalScripts.filter((f) => !!f.url).length;
  return (
    <div className="rounded-2xl border border-border/40 overflow-hidden bg-card shadow-sm" data-testid="section-final-results">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border/30 bg-emerald-500/[0.03]">
        <h2 className="text-base font-bold flex items-center gap-2" data-testid="heading-final-results">
          <Film className="h-4 w-4 text-emerald-500" />
          Final Videos
        </h2>
        <span className="text-[11px] font-medium text-muted-foreground tabular-nums" data-testid="text-final-count">
          {readyCount} ready
        </span>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {finalScripts.map(({ script, renderId, url, saveable, failed }) => {
          const matchedPlan = (scriptPlans || []).find((p: any) =>
            p.avatarId === script.avatarId && p.sortOrder === script.sortOrder
          );
          const displayTitle =
            matchedPlan?.title
            || (script.title && script.title !== "Untitled Script" ? script.title : null)
            || "Final video";
          const saved = savedRenderIds.has(renderId);
          const saving = savingRenderId === renderId;
          return (
            <div
              key={script.id}
              className="rounded-xl border border-border/30 bg-muted/[0.04] overflow-hidden"
              data-testid={`card-final-${script.id}`}
            >
              {url ? (
                <video
                  src={url}
                  controls
                  playsInline
                  className="w-full aspect-[9/16] object-contain bg-black"
                  data-testid={`video-final-${script.id}`}
                />
              ) : (
                <div
                  className="w-full aspect-[9/16] bg-muted/30 flex flex-col items-center justify-center gap-2 text-center px-4"
                  data-testid={`status-final-captioning-${script.id}`}
                >
                  {failed ? (
                    <>
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <p className="text-[11px] font-medium text-red-600">Captioning failed</p>
                      <p className="text-[10px] text-muted-foreground">Retry from the script's render panel.</p>
                    </>
                  ) : (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin text-fuchsia-500" />
                      <p className="text-[11px] font-medium text-muted-foreground">Adding captions…</p>
                    </>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                <p className="text-[12px] font-semibold truncate" data-testid={`text-final-title-${script.id}`}>
                  {displayTitle}
                </p>
                {(saveable || url) && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    {saveable && (
                      <button
                        type="button"
                        disabled={saved || saving}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSave(renderId);
                        }}
                        className="flex items-center gap-1 text-[10px] font-semibold text-violet-700 dark:text-violet-400 bg-violet-500/10 hover:bg-violet-500/20 disabled:opacity-70 disabled:cursor-default px-2 py-0.5 rounded-full transition-colors"
                        data-testid={`button-save-final-${script.id}`}
                        title={
                          saved
                            ? "Saved to My Ads"
                            : url
                              ? "Save to My Ads"
                              : "Save to My Ads (captions still generating — the current cut is saved)"
                        }
                      >
                        {saving ? (
                          <><Loader2 className="h-2.5 w-2.5 animate-spin" /> Saving…</>
                        ) : saved ? (
                          <><Check className="h-2.5 w-2.5" /> Saved</>
                        ) : (
                          <><Bookmark className="h-2.5 w-2.5" /> Save</>
                        )}
                      </button>
                    )}
                    {url && (
                      <a
                        href={url}
                        download
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 px-2 py-0.5 rounded-full transition-colors shrink-0"
                        data-testid={`link-final-mp4-${script.id}`}
                        title="Download final MP4"
                      >
                        <Download className="h-2.5 w-2.5" /> MP4
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
